import React, { useState, useEffect } from 'react';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import { Modal } from '@mui/material';
import RemoveUserModal from './RemoveUserModal';
import InviteUserModal from './InviteUserModal';
import InviteFailModal from './InviteFailModal';
import GroupAPI from 'api/GroupAPI';
import { useSnackbar } from 'notistack';
import EmailAPI from 'api/EmailAPI';
import UsersAPI from 'api/UsersAPI';
import { inviteUserNotification } from 'utils/NotificationsFuncs';
import LicenseEdit from 'components/Licenses/LicenseEdit';
import TeamsAPI from 'api/TeamsAPI';
import PaymentsAPI from 'api/PaymentsAPI';
import CreateTeamModal from './CreateTeamModal';

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#2E3036',
    ...theme.applyStyles('dark', {
      backgroundColor: '#2E3036',
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#1a90ff',
    ...theme.applyStyles('dark', {
      backgroundColor: '#308fe8',
    }),
  },
}));

interface ITeamContentProps {
  cur_user: any;
  handleTabChange: any;
  cusId: string;
}

const TeamContent: React.FC<ITeamContentProps> = ({ cur_user, handleTabChange, cusId }) => {
  const [ removeMode, setRemoveMode ] = useState<boolean>(false);
  const [ inviteMode, setInviteMode ] = useState<boolean>(false);
  const [ inviteFailMode, setInviteFailMode ] = useState<boolean>(false);
  const [ editMode, setEditMode ] = useState<boolean>(false);
  const [ newMode, setNewMode ] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const [ selectedUser, setSelectedUser ] = useState<any>({ isLoading: true, data: {} });
  const [ total, setTotal ] = useState<any>({ isLoading: true, data: 0 });
  const [ searchVal, setSearchVal ] = useState('');
  const [ members, setMembers ] = useState([]);
  const [ newTeamName, setNewTeamName ] = useState<string>('');
  const [ canCreate, setCanCreate ] = useState({ isLoading: true, data: false });

  const handleChange = (e: any) => {
    setSearchVal(e.target.value);
    if (!e.target.value) {
      handleReset();
    }
  };

  const handleSubmit = (): void => {
    setMembers((prevMembers) => {
      const lowerCaseQuery = searchVal.toLowerCase();

      // Sort members, placing matches first based on firstName, lastName, or email
      const sortedMembers = [ ...prevMembers ].sort((a: any, b: any): number => {
        const aMatches = [ a.firstName, a.lastName, a.email ].some((field) =>
          field.toLowerCase().includes(lowerCaseQuery)
        );
        const bMatches = [ b.firstName, b.lastName, b.email ].some((field) =>
          field.toLowerCase().includes(lowerCaseQuery)
        );

        if (aMatches && !bMatches) {
          return -1;
        } // a comes before b
        if (!aMatches && bMatches) {
          return 1;
        } // b comes before a
        return 0; // Keep original order if both match or both don't match
      });

      return sortedMembers;
    });
  };

  const handleReset = () => {
    setMembers(cur_user.selected_team.members);
  };

  const getTotalLicenses = () => {
    const customer = cur_user.stripe_cus_info.filter((cus: any) => cus.id === cusId);
    customer[0]?.subscriptions.data.map((sub: any) => {
      if (!sub.metadata.type || sub.metadata.type !== 'api') {
        sub.items.data.map((item: any) => {
          if (item?.metadata?.type === 'fixed') {
            setTotal({ isLoading: false, data: item.quantity });
          }
        });
      }
    });
  };

  useEffect(() => {
    getTotalLicenses();
  }, [ cusId ]);

  const validateEmail = (email: string) => {
    const isValidEmail = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;
    if(email && email.match(isValidEmail)){
      return true;
    }
    return false;
  };

  const sendInvite = async(formVals: any) => {
    const isValid = validateEmail(formVals.email);
    if (isValid && cur_user?.selected_team?.attributes?.pending) {
      // removes any extra whitespaces in email from input field
      formVals.email = formVals.email.trim();
      await EmailAPI.inviteUserToTeam({
        requestorEmail: cur_user?.basics?.email,
        teamID: cur_user.selected_team?.id,
        teamName: cur_user.selected_team?.name,
        formValues: formVals
      })
        .then(async(res) => {
          if (res?.success) {
            const res2 = await GroupAPI.addPendingInviteToGroup(cur_user.selected_team?.id, formVals);
            if (res2?.success) {
              enqueueSnackbar('Team invite sent via Email', { variant: 'success' });
            }
            const res3 = await UsersAPI.searchUsers(formVals.email);
            if (res3?.success) {
              if (res3.result.length > 0) {
                const userId = res3.result[0].id;
                await inviteUserNotification(cur_user, cur_user.selected_team, userId);
                enqueueSnackbar('User received notifaction to join your team', { variant: 'success' });
              }
              history.go(0);
            } else {
              enqueueSnackbar('Unable to send invite notification to user', { variant: 'error' });
            }
          } else {
            const message: string = res && res.result && typeof res.result === 'string' ? res.result : 'Unable to send team invite via Email';
            enqueueSnackbar(message, { variant: 'error' });
          }
        })
        .catch(() => {
          enqueueSnackbar('Unable to send team invite via Email', { variant: 'error' });
        });
    } else {
      enqueueSnackbar('Email is invalid', { variant: 'error' });
    }
  };

  const handleRemove = (user: string) => {
    setRemoveMode(true);
    setSelectedUser({ isLoading: false, data: user });
  };

  const handleEdit = (user: string) => {
    setEditMode(true);
    setSelectedUser({ isLoading: false, data: user });
  };

  const handleResend = async(email: string) => {
    await EmailAPI.inviteUserToTeam({
      requestorEmail: cur_user?.basics?.email,
      teamID: cur_user.selected_team?.id,
      teamName: cur_user.selected_team?.name,
      formValues: { email: email }
    })
      .then(async(res) => {
        if (res?.success) {
          const res3 = await UsersAPI.searchUsers(email);
          if (res3?.success) {
            if (res3.result.length > 0) {
              const userId = res3.result[0].id;
              await inviteUserNotification(cur_user, cur_user.selected_team, userId);
              enqueueSnackbar('User received notifaction to join your team', { variant: 'success' });
            }
          } else {
            enqueueSnackbar('Unable to send invite notification to user', { variant: 'error' });
          }
        } else {
          const message: string = res && res.result && typeof res.result === 'string' ? res.result : 'Unable to send team invite via Email';
          enqueueSnackbar(message, { variant: 'error' });
        }
      })
      .catch(() => {
        enqueueSnackbar('Unable to send team invite via Email', { variant: 'error' });
      });
  };

  const handleRevoke = async(email: string) => {
    const res = await GroupAPI.removePendingInvite({ group_id: cur_user.selected_team.id, email: email });
    if (res.success) {
      enqueueSnackbar('User invite has been revoked', { variant: 'success' });
      history.go(0);
    } else {
      enqueueSnackbar('Unable to revoke invite at this time', { variant: 'error' });
    }
  };


  const removeUserFromGroup = async(user_id: string, user_email: string) => {
    const resp = await GroupAPI.removeUserFromGroup(cur_user.selected_team.id, user_id);
    if(resp?.success){
      enqueueSnackbar('User removed successfully', { variant: 'info' });
      history.go(0);
    }else{
      const err_text = `Error removing user: ${resp?.result}`;
      enqueueSnackbar(err_text, { variant: 'error' });
    }
  };

  const checkCreate = () => {
    setCanCreate({ isLoading: true, data: false });
    const matched = cur_user.stripe_cus_info.filter((cus: any) => cus.id === cusId);
    if (matched[0]?.subscriptions?.data[0]?.status === 'trialing') {
      setCanCreate({ isLoading: false, data: false });
      return;
    }
    if (cur_user.selected_team.attributes.tier.includes('none') || cur_user.selected_team.attributes.tier.includes('standard')) {
      setCanCreate({ isLoading: false, data: false });
    }
    if (cur_user.selected_team.attributes.tier.includes('pro') && cur_user.teams.length < 5) {
      setCanCreate({ isLoading: false, data: true });
    }
    if (cur_user.selected_team.attributes.tier.includes('enterprise')) {
      setCanCreate({ isLoading: false, data: true });
    }
  };

  useEffect(() => {
    checkCreate();
    setMembers(cur_user.selected_team.members);
  }, []);

  const watchlistRoleId = process.env.REACT_APP_WATCHLIST_ID!;
  const cyberProtectionRoleId = process.env.REACT_APP_CYBER_PROTECTION_ID!;
  const biteListRoleId = process.env.REACT_APP_BITE_LIST_ID!;
  const tradeProtRoleId = process.env.REACT_APP_TRADE_PROTECTION_ID!;
  const biteScoreRoleId = process.env.REACT_APP_BITE_SCORE_ID!;
  const biteVizRoleId = process.env.REACT_APP_BITE_VIZ_ID!;

  const handleCreateNew = async(value: any) => {
    if (value === '') {
      return;
    }
    // removes any extra whitespaces in team name from input field
    const formattedName = value.trim();
    const new_team: any = {
      name: formattedName,
      team_admin_id: cur_user?.basics?.email,
      team_id: '',
      keycloakUserId: cur_user?.basics?.id
    };
    const resp = await TeamsAPI.createTeam({ newTeam: new_team });
    if(resp?.success && cur_user?.basics?.id){
      const teamId = resp.result.id;
      new_team.team_id = teamId;
      const addUser = await GroupAPI.assignGroupToUser(teamId, cur_user?.basics?.id);
      if (addUser?.success) {
        const createStripeCustomer = await PaymentsAPI.createCustomer(new_team);
        if (createStripeCustomer?.success) {
          const addWatchlistRole = await GroupAPI.addRoleToGroup(teamId, watchlistRoleId, 'watchlist-data');
          if (addWatchlistRole?.success) {
            const addCyberRole = await GroupAPI.addRoleToGroup(teamId, cyberProtectionRoleId, 'cyber-protection');
            if (addCyberRole?.success) {
              const addBiteListRole = await GroupAPI.addRoleToGroup(teamId, biteListRoleId, 'bite-list');
              if (addBiteListRole?.success) {
                const addTradeProtRole = await GroupAPI.addRoleToGroup(teamId, tradeProtRoleId, 'trade-protection');
                if (addTradeProtRole?.success) {
                  const addBiteScoreRole = await GroupAPI.addRoleToGroup(teamId, biteScoreRoleId, 'bite-score');
                  if (addBiteScoreRole?.success) {
                    const addBiteVizRole = await GroupAPI.addRoleToGroup(teamId, biteVizRoleId, 'customizable');
                    if (addBiteVizRole?.success) {
                      const error_msg = 'New team created';
                      enqueueSnackbar(error_msg, { variant: 'success' });
                      history.go(0);
                    }
                  }
                }
              }
            }
          }
        }
      }
    } else {
      const error_msg = 'Error creating new team. Team name may already be in use';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  return (
    <div className='team-content-container'>
      {cur_user.selected_team.attributes.team_admin.includes(cur_user.basics.email) && (
        <div className='team-content-header'>
          {/* BUILD LATER */}
          <div className='team-content-search'>
            <div className='screening-input-field-container'>
              <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                <path d='M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z' stroke='#BDBEC1' strokeWidth='1.66667' strokeLinecap='round' strokeLinejoin='round'/>
              </svg>
              <input
                placeholder='Search users'
                id='primary'
                type='text'
                className='screening-input-field-content'
                name='primary'
                value={searchVal}
                onChange={(e) => handleChange(e)}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmit();
                  }
                }}
              />
            </div>
          </div>
          {cur_user.selected_team.members.length === total.data && (
            <div className='team-content-invite-user' onClick={() => setInviteFailMode(true)}>
              <div className='team-content-invite-user-text'>
                + Invite User
              </div>
            </div>
          )}
          {cur_user.selected_team.members.length < total.data && (
            <div className='team-content-invite-user' onClick={() => setInviteMode(true)}>
              <div className='team-content-invite-user-text'>
                + Invite User
              </div>
            </div>
          )}
          {!canCreate.isLoading && canCreate.data === true && (
            <div className='create-new-team-btn' onClick={() => setNewMode(true)}>
              <div className='create-new-team-btn-text'>
                + Create New Team
              </div>
            </div>
          )}
        </div>
      )}
      <div className='team-content-table'>
        <div className='team-table-header'>
          <div className='team-table-header-content'>
            <div className='team-table-header-text-group'>
              <div className='team-table-header-text-group-spacing'>
                <div className='team-table-header-title'>
                  {cur_user?.selected_team?.name}
                </div>
                {cur_user.selected_team.attributes.team_admin.includes(cur_user.basics.email) && (
                  <>
                    {!total.isLoading && (
                      <>
                        <div className='team-table-header-bar'>
                          <BorderLinearProgress
                            variant='determinate'
                            value={cur_user.selected_team.members.length / total.data * 100}
                          />
                        </div>
                        <div className='team-table-seats-used'>
                          {cur_user.selected_team.members.length} of {total.data} seats used
                        </div>
                      </>
                    )}
                    <div className='team-table-buy-seats-btn' onClick={() => handleTabChange('Plans')}>
                      <div className='team-table-buy-seats-text'>
                        Buy More Seats
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className='team-table-body-header'>
          <div className='team-table-body-name-column'>
            <div className='team-table-body-column-inner'>
              <div className='team-table-body-column-text'>
                Name
              </div>
            </div>
          </div>
          <div className='team-table-body-name-column'>
            <div className='team-table-body-column-inner'>
              <div className='team-table-body-column-text'>
                Email
              </div>
            </div>
          </div>
          <div className='team-table-body-perms-column'>
            <div className='team-table-body-column-inner'>
              <div className='team-table-body-column-text'>
                Permissions
              </div>
            </div>
          </div>
          <div className='team-table-body-placeholder-column'></div>
        </div>
        {cur_user?.selected_team?.attributes?.pending?.slice(1)?.map((email: any, index: number) => (
          <div className='team-table-member-row' key={index}>
            <div className={'team-table-row-name'}>
              <div className='team-table-row-text'>
                {''}
              </div>
            </div>
            <div className={'team-table-row-name'}>
              <div className='team-table-row-text'>
                {email}
              </div>
            </div>
            <div className={'team-table-row-perms'}>
              <div className='team-table-row-badge'>
                <svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8' fill='none'>
                  <circle cx='4.00002' cy='4' r='3' fill='#BDBEC1'/>
                </svg>
                <div className='team-table-row-text'>
                  Pending
                </div>
              </div>
            </div>
            <div className={'team-table-row-placeholder'}>
              {cur_user.selected_team.attributes.team_admin.includes(cur_user.basics.email) && <div className='team-table-row-placeholder-btn' onClick={() => handleResend(email)}>
                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='19' viewBox='0 0 24 19' fill='none'>
                  <path d='M15.7927 16.0926C15.7927 16.0926 15.5913 14.7839 15.5913 13.8874C15.5913 12.991 15.9392 12.0946 16.644 11.4044C18.0444 10.0329 20.3053 10.0329 21.7057 11.4044C23.1062 12.7759 23.1062 14.99 21.7057 16.3615C20.5524 17.491 18.8041 17.6882 17.4495 16.9532M14.1176 14.4342L15.8019 16.0837L17.4861 14.4342' stroke='#BEBEC1' strokeWidth='1.3' strokeLinecap='round' strokeLinejoin='round'/>
                  <path d='M11.8099 13.6809H4.9079C3.54052 13.6809 2.8487 13.6809 2.42546 13.4755C2.16501 13.3491 1.93711 13.1675 1.76619 12.9463L7.02408 8.33333L7.49614 8.65719C8.12286 9.08373 8.4647 9.3128 8.90421 9.41548C9.28675 9.51027 9.70185 9.51027 10.0844 9.41548C10.5239 9.3128 10.8657 9.07583 11.5006 8.64929L11.9564 8.34123L13.2098 9.43918C13.511 9.10742 13.8447 8.80727 14.2191 8.5466L13.104 7.56714L17.6375 4.48657V7.50395C18.1096 7.50395 18.5572 7.56714 18.9967 7.67772V4.76303C18.9967 4.21011 18.9967 3.75197 18.9805 3.36493C19.013 3.24645 19.0049 3.12006 18.956 2.99368C18.9154 2.49605 18.8258 2.109 18.6305 1.75355C18.3131 1.14534 17.8084 0.655608 17.1736 0.347551C16.4655 0 15.6678 0 14.0807 0H4.9079C3.32077 0 2.52313 2.35405e-08 1.81503 0.35545C1.18831 0.671406 0.683687 1.15324 0.358122 1.76935C0.170922 2.1248 0.0813914 2.51185 0.0406957 3.00948C0 3.12796 -0.00813914 3.25434 0.0162783 3.38073C0 3.75197 0 4.20221 0 4.75513V10.2291C0 11.7773 0 12.5513 0.358122 13.2385C0.675548 13.8468 1.18017 14.3365 1.80689 14.6445C2.51499 15 3.31263 15 4.89976 15H12.111C11.9564 14.5814 11.8506 14.139 11.8017 13.6809H11.8099ZM1.57085 2.35387C1.75805 1.99052 2.0592 1.70616 2.4336 1.51659C2.85684 1.31122 3.54052 1.31122 4.91604 1.31122H14.0888C15.4562 1.31122 16.148 1.31122 16.5713 1.51659C16.9457 1.69826 17.2387 1.99052 17.434 2.35387C17.5154 2.50395 17.5643 2.68562 17.5887 2.91469L11.6634 6.94313C11.6634 6.94313 11.6308 6.96682 11.6145 6.97472L10.7274 7.57504C10.239 7.90679 9.9623 8.08847 9.7751 8.13586C9.60418 8.17535 9.41698 8.17535 9.24606 8.13586C9.05072 8.08847 8.78213 7.90679 8.2775 7.56714L7.39034 6.96682C7.39034 6.96682 7.37406 6.95103 7.35778 6.95103L1.41621 2.91469C1.44877 2.67773 1.4976 2.49605 1.57085 2.35387ZM1.35924 10.237V4.48657L5.89273 7.56714L1.36737 11.5245C1.3511 11.1848 1.3511 10.7583 1.3511 10.237H1.35924Z' fill='#BEBEC1'/>
                </svg>
              </div>}
              {cur_user.selected_team.attributes.team_admin.includes(cur_user.basics.email) && <div className='team-table-row-placeholder-btn' onClick={() => handleRevoke(email)}>
                <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                  <path d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167' stroke='#BDBEC1' strokeWidth='1.66667' strokeLinecap='round' strokeLinejoin='round'/>
                </svg>
              </div>}
            </div>
          </div>
        ))}
        {members.map((member: any, index: number) => (
          <div className='team-table-member-row' key={member.id}>
            <div className={index === cur_user.selected_team.members.length - 1 ? 'team-table-row-name last-row' : 'team-table-row-name'}>
              <div className='team-table-row-text'>
                {member.firstName ? member.firstName : ''} {member.lastName ? member.lastName : ''}
              </div>
            </div>
            <div className={index === cur_user.selected_team.members.length - 1 ? 'team-table-row-name last-row' : 'team-table-row-name'}>
              <div className='team-table-row-text'>
                {member.email ? member.email : ''}
              </div>
            </div>
            <div className={index === cur_user.selected_team.members.length - 1 ? 'team-table-row-perms last-row' : 'team-table-row-perms'}>
              {cur_user.selected_team.attributes.team_admin.includes(member.email) ? (
                <div className='team-table-row-badge'>
                  <svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8' fill='none'>
                    <circle cx='4.00002' cy='4' r='3' fill='#85E13A'/>
                  </svg>
                  <div className='team-table-row-text'>
                    Admin
                  </div>
                </div>
              ) : (
                <div className='team-table-row-badge'>
                  <svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8' fill='none'>
                    <circle cx='4.00002' cy='4' r='3' fill='#4CC9F0'/>
                  </svg>
                  <div className='team-table-row-text'>
                    Contributor
                  </div>
                </div>
              )}
            </div>
            {cur_user.selected_team.attributes.team_admin.includes(cur_user.basics.email) ? (
              <div className={index === cur_user.selected_team.members.length - 1 ? 'team-table-row-placeholder last-row' : 'team-table-row-placeholder'}>
                <div className='team-table-row-placeholder-btn' onClick={() => handleEdit(member)}>
                  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                    <path d='M10 16.6667H17.5M2.50003 16.6667H3.89548C4.30313 16.6667 4.50696 16.6667 4.69877 16.6206C4.86883 16.5798 5.03141 16.5125 5.18053 16.4211C5.34872 16.318 5.49285 16.1739 5.7811 15.8856L16.2501 5.41669C16.9404 4.72634 16.9404 3.60705 16.2501 2.91669C15.5597 2.22634 14.4404 2.22634 13.7501 2.91669L3.28108 13.3856C2.99283 13.6739 2.8487 13.818 2.74563 13.9862C2.65425 14.1353 2.58691 14.2979 2.54608 14.468C2.50003 14.6598 2.50003 14.8636 2.50003 15.2713V16.6667Z' stroke='#BDBEC1' strokeWidth='1.66667' strokeLinecap='round' strokeLinejoin='round'/>
                  </svg>
                </div>
                {!cur_user.selected_team.attributes.team_admin.includes(member.email) ? (
                  <div className='team-table-row-placeholder-btn' onClick={() => handleRemove(member)}>
                    <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
                      <path d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167' stroke='#BDBEC1' strokeWidth='1.66667' strokeLinecap='round' strokeLinejoin='round'/>
                    </svg>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            ) : (
              <div className={index === cur_user.selected_team.members.length - 1 ? 'team-table-row-placeholder last-row' : 'team-table-row-placeholder'}></div>
            )}
          </div>
        ))}
      </div>
      <>
        <Modal
          open={removeMode}
          onClose={() => setRemoveMode(false)}
        >
          <RemoveUserModal removeUserFromGroup={removeUserFromGroup} user={selectedUser.data} setRemoveMode={setRemoveMode} />
        </Modal>
        <Modal
          open={inviteMode}
          onClose={() => setInviteMode(false)}
        >
          <InviteUserModal user={selectedUser.data} setInviteMode={setInviteMode} sendInvite={sendInvite} />
        </Modal>
        <Modal
          open={inviteFailMode}
          onClose={() => setInviteFailMode(false)}
        >
          <InviteFailModal setInviteFailMode={setInviteFailMode} handleTabChange={handleTabChange} />
        </Modal>
        <Modal
          open={editMode}
          onClose={() => setEditMode(false)}
        >
          <LicenseEdit handleTabChange={handleTabChange} team={cur_user.selected_team} user={selectedUser.data} setEditMode={setEditMode} />
        </Modal>
        <Modal
          open={newMode}
          onClose={() => setNewMode(false)}
        >
          <CreateTeamModal
            setNewMode={setNewMode}
            newTeamName={newTeamName}
            setNewTeamName={setNewTeamName}
            handleCreateNew={handleCreateNew}
          />
        </Modal>
      </>
      {/* )} */}
    </div>
  );
};

export default TeamContent;
